import { useMutation, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '@config/server/apiClient';
import { inventoryEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

export interface PublishInventoryResponse {
	published: number;
	total: number;
}

export interface PublishInventoryParams {
	servicesProviderId: string;
	inventoryIds?: string[];
}

/**
 * API function to publish draft inventory items for a specific services provider
 * When inventoryIds is provided, only those items will be published
 * When inventoryIds is empty or not provided, all draft items will be published
 * @param servicesProviderId - Required services provider ID
 * @param inventoryIds - Optional array of specific inventory item IDs to publish
 */
export const publishInventory = async (
	servicesProviderId: string,
	inventoryIds?: string[],
): Promise<PublishInventoryResponse> => {
	const response = await apiClient.post<PublishInventoryResponse>(
		inventoryEndpoints.publishAll(servicesProviderId),
		{ inventoryIds },
	);
	return response.data;
};

/**
 * Hook for publishing draft inventory items
 * When inventoryIds is provided, only those items will be published
 * When inventoryIds is empty or not provided, all draft items will be published
 */
export const usePublishInventoryMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ servicesProviderId, inventoryIds }: PublishInventoryParams) =>
			publishInventory(servicesProviderId, inventoryIds),
		onSuccess: () => {
			// Invalidate the inventory list and detail queries to ensure they get fresh data
			queryClient.invalidateQueries({
				queryKey: queries.inventory.list._def,
			});
			queryClient.invalidateQueries({
				queryKey: queries.inventory.detail._def,
			});
		},
	});
};
